'use client';

import { createContext, useContext } from 'react';

import type { FeatureFlags } from '../config/features';

type BooleanFeatureFlagKey = {
  [K in keyof FeatureFlags]: FeatureFlags[K] extends boolean ? K : never;
}[keyof FeatureFlags];

interface FeatureFlagsContextValue {
  features: FeatureFlags;
  isFeatureEnabled: (feature: BooleanFeatureFlagKey) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
  undefined,
);

interface FeatureFlagsProviderProps {
  children: React.ReactNode;
  features: FeatureFlags;
}

export const FeatureFlagsProvider = ({
  children,
  features,
}: FeatureFlagsProviderProps) => {
  const isFeatureEnabled = (feature: BooleanFeatureFlagKey): boolean => {
    return features[feature] ?? false;
  };

  return (
    <FeatureFlagsContext.Provider value={{ features, isFeatureEnabled }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Хук для использования feature flags в компонентах
 * @example
 * const { isFeatureEnabled } = useFeatureFlags();
 * if (isFeatureEnabled('enableMaskedPhone')) {
 *   // показываем новый функционал
 * }
 */
export const useFeatureFlags = (): FeatureFlagsContextValue => {
  const context = useContext(FeatureFlagsContext);

  if (!context) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider',
    );
  }

  return context;
};
